import { useState } from 'react';

export default function MigrationPage() {
  const [showCallbackModal, setShowCallbackModal] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [showCustomerToolsDropdown, setShowCustomerToolsDropdown] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const form = e.currentTarget;
      const formDataObj = new FormData(form);

      // Textarea karakter sınırı kontrolü
      const message = formDataObj.get('message') as string;
      if (message && message.length > 500) {
        setSubmitStatus({
          type: 'error',
          message: 'Mesaj 500 karakterden uzun olamaz.',
        });
        setIsSubmitting(false);
        return;
      }

      // Supabase Edge Function'a gönder
      const response = await fetch(
        'https://lbvcnepwlojmojlxmhau.supabase.co/functions/v1/send-contact-email',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            fullName: formDataObj.get('fullName'),
            phone: formDataObj.get('phone'),
            email: formDataObj.get('email'),
            company: formDataObj.get('company'),
            message: formDataObj.get('message'),
          }),
        }
      );

      let result: any;
      try {
        result = await response.json();
      } catch (jsonError) {
        throw new Error('Sunucudan geçerli bir yanıt alınamadı.');
      }

      if (result?.success) {
        setSubmitStatus({
          type: 'success',
          message:
            'Talebiniz başarıyla gönderildi. En kısa sürede sizinle iletişime geçeceğiz.',
        });
        form.reset();
        setTimeout(() => {
          setShowCallbackModal(false);
          setSubmitStatus(null);
        }, 2000);
      } else {
        throw new Error(result?.error || 'Form gönderimi başarısız');
      }
    } catch (error) {
      console.error('Form submit error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Form gönderilirken bir hata oluştu. Lütfen tekrar deneyin.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const migrationServices = [
    {
      icon: 'ri-server-line',
      title: 'Sunucu Taşıma',
      description: 'Fiziksel ve sanal sunucularınızı güvenle yeni ortamlara taşıyoruz. Downtime minimizasyonu ile kesintisiz geçiş sağlıyoruz.',
      features: ['Fiziksel sunucu taşıma', 'Sanal ortam geçişi', 'Hibrit bulut migrasyonu', 'Minimum kesinti süresi']
    },
    {
      icon: 'ri-database-2-line',
      title: 'Veritabanı Geçişi',
      description: 'SQL Server, MySQL, PostgreSQL ve diğer veritabanlarınızı güvenle taşıyoruz. Veri bütünlüğü garantili.',
      features: ['SQL Server Migration', 'MySQL/PostgreSQL geçişi', 'Veri doğrulama', 'Performance optimizasyonu']
    },
    {
      icon: 'ri-cloud-line',
      title: 'Bulut Geçişi',
      description: 'On-premise sistemlerinizi Azure, AWS veya diğer bulut platformlarına profesyonel şekilde taşıyoruz.',
      features: ['Azure Migration', 'AWS geçiş hizmetleri', 'Hibrit bulut kurulumu', 'Maliyet optimizasyonu']
    },
    {
      icon: 'ri-mail-line',
      title: 'E-posta Sistemi Geçişi',
      description: 'Exchange, Office 365, Gmail ve diğer e-posta sistemleri arasında sorunsuz geçiş sağlıyoruz.',
      features: ['Exchange geçişi', 'Office 365 Migration', 'PST/OST dönüşümü', 'Kullanıcı eğitimi']
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Güvenlik ve Backup',
      description: 'Taşıma sürecinde tüm verilerinizi yedekliyor ve güvenlik önlemlerini alıyoruz.',
      features: ['Kapsamlı backup', 'Veri şifreleme', 'Güvenlik testleri', 'Recovery planning']
    },
    {
      icon: 'ri-settings-line',
      title: 'Uygulama Migrasyonu',
      description: 'İş uygulamalarınızı, web sitelerinizi ve yazılımlarınızı yeni ortamlara taşıyoruz.',
      features: ['Web uygulamaları', 'Özel yazılımlar', 'Legacy sistem geçişi', 'API entegrasyonları']
    }
  ];

  const migrationProcess = [
    {
      step: '01',
      title: 'Analiz ve Planlama',
      description: 'Mevcut altyapınızı detaylı şekilde analiz ediyor, risk değerlendirmesi yapıyor ve kapsamlı geçiş planı hazırlıyoruz.',
      icon: 'ri-search-line'
    },
    {
      step: '02',
      title: 'Test Ortamı Kurulumu',
      description: 'Geçiş öncesi test ortamında tüm süreçleri simüle ediyor, olası sorunları önceden tespit ediyoruz.',
      icon: 'ri-flask-line'
    },
    {
      step: '03',
      title: 'Backup ve Güvenlik',
      description: 'Tüm kritik verilerinizi yedekliyor, geçiş sırasında veri güvenliğini en üst seviyede tutuyoruz.',
      icon: 'ri-shield-check-line'
    },
    {
      step: '04',
      title: 'Geçiş İşlemi',
      description: 'Planlanan zamanda geçiş işlemini gerçekleştiriyor, süreç boyunca anlık raporlama yapıyoruz.',
      icon: 'ri-upload-cloud-2-line'
    },
    {
      step: '05',
      title: 'Test ve Doğrulama',
      description: 'Geçiş sonrası tüm sistemleri test ediyor, veri bütünlüğünü ve performansı doğruluyoruz.',
      icon: 'ri-check-double-line'
    },
    {
      step: '06',
      title: 'Optimizasyon',
      description: 'Yeni ortamda performans optimizasyonu yapıyor, kullanıcı eğitimleri ve dokümantasyon sağlıyoruz.',
      icon: 'ri-speed-up-line'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white text-gray-900 sticky top-0 z-50 shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-8">
              <a href="/" className="flex items-center space-x-3 cursor-pointer">
                <img
                  src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                  alt="Nimbus Bilişim Logo"
                  className="h-24 w-auto"
                />
                <div className="text-lg text-gray-900">
                  Nimbus Bulut Bilişim Teknoloji ve Mühendislik
                </div>
              </a>
              <nav className="hidden lg:flex space-x-8">
                <div className="relative group">
                  <button
                    onClick={() => {
                      setShowServicesDropdown(!showServicesDropdown);
                      setShowCustomerToolsDropdown(false);
                    }}
                    className="flex items-center space-x-1 hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    <span>IT Hizmetleri</span>
                    <i
                      className={`ri-arrow-${showServicesDropdown ? 'up' : 'down'}-s-line transition-transform`}
                    ></i>
                  </button>
                  {showServicesDropdown && (
                    <div className="absolute top-full left-0 mt-2 w-[1000px] bg-white rounded-lg shadow-lg border border-gray-200 py-6 z-50">
                      <div className="grid grid-cols-3 gap-8 px-8">
                        {/* IT Destek Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            IT Destek Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/it-support');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">IT Destek & Bakım Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">7/24 IT destek ve bakım hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/it-consultinging');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">IT Danışmanlık Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Profesyonel danışmanlık</div>
                            </button>
                          </div>
                        </div>

                        {/* Bulut Bilişim Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            Bulut Bilişim Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/virtual-server');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Bulut Bilişim Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Kurumsal bulut sunucu hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/managed-it');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Yönetilen IT Hizmetleri</div>
                              <div className="text-sm text-gray-500 mt-1">Tüm yönetim hizmetleri</div>
                            </button>
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/migration');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-blue-600 font-semibold cursor-pointer"
                            >
                              <div className="font-medium">Sistem Taşıma ve Geçiş Hizmetleri (Migration)</div>
                              <div className="text-sm text-gray-500 mt-1">Sunucu taşıma hizmetleri</div>
                            </button>
                          </div>
                        </div>

                        {/* Microsoft 365 Hizmetleri */}
                        <div>
                          <h4 className="text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide">
                            Microsoft 365 Hizmetleri
                          </h4>
                          <div className="space-y-3">
                            <button
                              onClick={() => {
                                window.REACT_APP_NAVIGATE('/services/microsoft-365');
                                setShowServicesDropdown(false);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                            >
                              <div className="font-medium">Microsoft 365 Geçiş ve Yönetimi</div>
                              <div className="text-sm text-gray-500 mt-1">Azure Expert hizmetleri</div>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  Başarı Hikayeleri
                </button>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/about')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  Hakkımızda
                </button>
                <button
                  onClick={() => window.REACT_APP_NAVIGATE('/')}
                  className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                >
                  SSS
                </button>
              </nav>
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden lg:flex items-center space-x-4 ml-8">
                <div className="relative group">
                  <button
                    onClick={() => {
                      setShowCustomerToolsDropdown(!showCustomerToolsDropdown);
                      setShowServicesDropdown(false);
                    }}
                    className="flex items-center space-x-1 hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    <span>Müşteri Araçları</span>
                    <i
                      className={`ri-arrow-${showCustomerToolsDropdown ? 'up' : 'down'}-s-line transition-transform`}
                    ></i>
                  </button>
                  {showCustomerToolsDropdown && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <button
                        onClick={() => window.REACT_APP_NAVIGATE('/')}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer"
                      >
                        <div className="font-medium">Müşteri Portalı</div>
                        <div className="text-sm text-gray-500">
                          Fatura ve destek yönetimi
                        </div>
                      </button>
                      <a
                        href="mailto:destek@nimbusbilisim.com"
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer block"
                      >
                        <div className="font-medium">7/24 Destek Hattı</div>
                        <div className="text-sm text-gray-500">
                          destek@nimbusbilisim.com
                        </div>
                      </a>
                      <a
                        href="https://rustdesk.com/download"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer block"
                      >
                        <div className="font-medium">RustDesk İndir</div>
                        <div className="text-sm text-gray-500">
                          Uzaktan destek programı
                        </div>
                      </a>
                      <button
                        onClick={() => {
                          setShowCallbackModal(true);
                          setShowCustomerToolsDropdown(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700 cursor-pointer"
                      >
                        <div className="font-medium">Geri Arama Talebi</div>
                        <div className="text-sm text-gray-500">
                          Uzmanlarımızla görüşün
                        </div>
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowCallbackModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                İletişim
              </button>
              <a
                href="https://www.linkedin.com/company/nimbus-bulut-bili%C5%9Fim-teknoloji-yaz%C4%B1l%C4%B1m-ve-m%C3%BChendislik-a-%C5%9F/?viewAsMember=true"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-700 hover:text-blue-600 transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill text-2xl"></i>
              </a>
              <button
                onClick={() => setShowMobileMenu(true)}
                className="lg:hidden text-gray-700 cursor-pointer"
              >
                <i className="ri-menu-line text-2xl"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Click outside to close dropdowns */}
      {(showServicesDropdown || showCustomerToolsDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowServicesDropdown(false);
            setShowCustomerToolsDropdown(false);
          }}
        ></div>
      )}

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setShowMobileMenu(false)}
          ></div>
          <div className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-bold text-gray-900">Menü</h3>
                <button
                  onClick={() => setShowMobileMenu(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <nav className="space-y-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    IT Hizmetleri
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/it-support');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">
                        IT Destek & Bakım Hizmetleri
                      </div>
                      <div className="text-sm text-gray-500">
                        7/24 IT destek ve bakım hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">IT Danışmanlık Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        Profesyonel danışmanlık
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/virtual-server');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Bulut Bilişim Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        Kurumsal bulut sunucu hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/managed-it');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Yönetilen IT Hizmetleri</div>
                      <div className="text-sm text-gray-500">
                        Tüm yönetim hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/microsoft-365');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Microsoft 365 Geçiş ve Yönetimi</div>
                      <div className="text-sm text-gray-500">
                        Azure Expert hizmetleri
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/services/migration');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-blue-600 font-semibold cursor-pointer"
                    >
                      <div className="font-medium">Sistem Taşıma ve Geçiş</div>
                      <div className="text-sm text-blue-500">
                        Sunucu taşıma hizmetleri
                      </div>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    Şirket
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      Başarı Hikayeleri
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/about');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      Hakkımızda
                    </button>
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      SSS
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase mb-3">
                    Müşteri Araçları
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        window.REACT_APP_NAVIGATE('/');
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Müşteri Portalı</div>
                      <div className="text-sm text-gray-500">
                        Fatura ve destek yönetimi
                      </div>
                    </button>
                    <a
                      href="mailto:destek@nimbusbilisim.com"
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer block"
                    >
                      <div className="font-medium">7/24 Destek Hattı</div>
                      <div className="text-sm text-gray-500">
                        destek@nimbusbilisim.com
                      </div>
                    </a>
                    <a
                      href="https://rustdesk.com/download"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer block"
                    >
                      <div className="font-medium">RustDesk İndir</div>
                      <div className="text-sm text-gray-500">
                        Uzaktan destek programı
                      </div>
                    </a>
                    <button
                      onClick={() => {
                        setShowCallbackModal(true);
                        setShowMobileMenu(false);
                      }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 rounded-lg transition-colors text-gray-700 cursor-pointer"
                    >
                      <div className="font-medium">Geri Arama Talebi</div>
                      <div className="text-sm text-gray-500">
                        Uzmanlarımızla görüşün
                      </div>
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <button
                    onClick={() => {
                      setShowCallbackModal(true);
                      setShowMobileMenu(false);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    İletişim
                  </button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-20 overflow-hidden">
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/ced327f01568826f4571012586673f2e.jpg')`,
            animation: 'slowPan 5s ease-in-out infinite alternate'
          }}
        ></div>
        <style>{`
          @keyframes slowPan {
            0% {
              transform: scale(1) translateX(0);
            }
            100% {
              transform: scale(1.1) translateX(-5%);
            }
          }
        `}</style>
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-4xl">
            <p className="text-blue-400 text-lg mb-4">
              Sistem Geçiş Uzmanları
            </p>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Sistem Taşıma ve Geçiş Hizmetleri
            </h1>
            <p className="text-xl mb-8 leading-relaxed">
              Nimbus Bilişim olarak, işletmenizin IT altyapısını güvenle yeni ortamlara taşıyoruz. 
              Sunuculardan veritabanlarına, e-posta sistemlerinden bulut geçişine kadar 
              tüm migration süreçlerinde uzman ekibimizle yanınızdayız.
            </p>
            <button
              onClick={() => setShowCallbackModal(true)}
              className="bg-blue-600 hover:bg-blue-700 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center space-x-2 whitespace-nowrap cursor-pointer"
            >
              <span>Ücretsiz Analiz Alın</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Migration Services Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Kapsamlı Migration Hizmetlerimiz
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Her türlü sistem geçişinde deneyimli ekibimizle güvenli, hızlı ve kesintisiz 
              taşıma işlemleri gerçekleştiriyoruz.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {migrationServices.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-lg flex items-center justify-center">
                  <i className={`${service.icon} text-blue-600 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-4 text-center">
                  {service.description}
                </p>
                <ul className="space-y-2">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center space-x-2 text-sm text-gray-600">
                      <i className="ri-check-line text-blue-600 flex-shrink-0"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Migration Process Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Migration Sürecimiz
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              6 adımlık profesyonel migration sürecimizle sistemlerinizi güvenle taşıyoruz.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {migrationProcess.map((step, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-sm p-6 relative"
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">
                    {step.step}
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <i className={`${step.icon} text-blue-600 text-lg`}></i>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-600">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Neden Nimbus Bilişim Migration Hizmetleri?
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=professional%20IT%20team%20working%20on%20server%20migration%20project%2C%20modern%20data%20center%20with%20cloud%20infrastructure%2C%20technical%20experts%20managing%20system%20transition%20with%20advanced%20monitoring%20tools&width=600&height=500&seq=31&orientation=landscape"
                alt="Migration Uzmanları"
                className="w-full rounded-lg shadow-lg"
              />
            </div>
            <div>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Minimum Kesinti Süresi
                    </h3>
                    <p className="text-gray-600">
                      Gelişmiş planlama ve test süreçlerimizle kesinti sürelerini minimum seviyede tutuyoruz.
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      %100 Veri Güvenliği
                    </h3>
                    <p className="text-gray-600">
                      Tüm verilerinizi şifreleyerek ve kapsamlı backup'lar alarak güvenliğinizi garanti ediyoruz.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-team-line text-purple-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Uzman Ekip
                    </h3>
                    <p className="text-gray-600">
                      15+ yıl deneyimli migration uzmanlarımız her aşamada size rehberlik eder.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-24-hours-line text-orange-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      7/24 Destek
                    </h3>
                    <p className="text-gray-600">
                      Migration süreci ve sonrasında 7/24 teknik destek sağlıyoruz.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-guarantee-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Başarı Garantisi
                    </h3>
                    <p className="text-gray-600">
                      Migration işleminin başarıyla tamamlanması için garanti veriyoruz.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-16 bg-blue-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Migration Projeniz İçin Hemen Başlayalım
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Sistem taşıma ve geçiş işlemleriniz için ücretsiz analiz ve danışmanlık hizmeti alın. 
            Uzman ekibimiz size en uygun çözümü sunar.
          </p>
          <button
            onClick={() => setShowCallbackModal(true)}
            className="bg-white hover:bg-gray-100 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold transition-colors flex items-center space-x-2 mx-auto whitespace-nowrap cursor-pointer"
          >
            <span>Ücretsiz Danışmanlık Alın</span>
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white text-gray-900 py-12 border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <img
                  src="https://static.readdy.ai/image/ceb81aa66be17b1fc7ed204b9f91964c/7b7be6e694aa19d5c5f7abd74de9f9de.png"
                  alt="Nimbus Bilişim Logo"
                  className="h-24 w-auto"
                />
                <div className="text-lg">Nimbus Bulut Bilişim Teknoloji ve Mühendislik</div>
              </div>
              <p className="text-gray-600 mb-4">
                Türkiye'nin güvenilir IT çözüm ortağı. İşletmenizin teknoloji
                ihtiyaçlarında yanımızdayız.
              </p>
              <div className="flex space-x-4">
                <a
                  href="#"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a
                  href="#"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <i className="ri-twitter-fill text-xl"></i>
                </a>
                <a
                  href="#"
                  className="text-gray-600 hover:text-blue-600 transition-colors"
                >
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Hizmetler</h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <button
                    onClick={() =>
                      window.REACT_APP_NAVIGATE('/services/virtual-server')
                    }
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Sanal Sunucu
                  </button>
                </li>
                <li>
                  <button
                    onClick={() =>
                      window.REACT_APP_NAVIGATE('/services/microsoft-365')
                    }
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Microsoft 365
                  </button>
                </li>
                <li>
                  <button
                    onClick={() =>
                      window.REACT_APP_NAVIGATE('/services/it-support')
                    }
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    IT Destek
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/services/migration')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Sistem Taşıma
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Şirket</h4>
              <ul className="space-y-2 text-gray-600">
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/about')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Hakkımızda
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Başarı Hikayeleri
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors whitespace-nowrap text-gray-700 cursor-pointer"
                  >
                    SSS
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => window.REACT_APP_NAVIGATE('/')}
                    className="hover:text-blue-600 transition-colors cursor-pointer"
                  >
                    Müşteri Portalı
                  </button>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">İletişim</h4>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center space-x-2">
                  <i className="ri-mail-line"></i>
                  <span>info@nimbusbilisim.com</span>
                </li>
                <li className="flex items-center space-x-2">
                  <i className="ri-map-pin-line"></i>
                  <span>İzmir, Türkiye</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-10 pt-10 text-center text-gray-600">
            <p>
              © 2025 Nimbus Bulut Bilişim Teknoloji ve Mühendislik. Tüm hakları saklıdır. |{' '}
              <a
                href="https://readdy.ai/?origin=logo"
                className="hover:text-blue-600 transition-colors"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>

      {/* Callback Modal */}
      {showCallbackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  Migration Danışmanlığı
                </h3>
                <button
                  onClick={() => {
                    setShowCallbackModal(false);
                    setSubmitStatus(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              {submitStatus && (
                <div
                  className={`mb-6 p-4 rounded-lg ${
                    submitStatus.type === 'success'
                      ? 'bg-green-50 text-green-800 border border-green-200'
                      : 'bg-red-50 text-red-800 border border-red-200'
                  }`}
                >
                  {submitStatus.message}
                </div>
              )}

              <form
                onSubmit={handleFormSubmit}
                data-readdy-form
                id="migration-contact-form"
              >
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="fullName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Adınız ve soyadınız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Telefon numaranız"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="E-posta adresiniz"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Şirket
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Şirket adınız (opsiyonel)"
                      onChange={handleInputChange}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Migration İhtiyacınız
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                      placeholder="Hangi sistemleri taşımak istiyorsunuz? (maksimum 500 karakter)"
                      onChange={handleInputChange}
                    ></textarea>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white py-3 rounded-lg transition-colors mt-6 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Gönderiliyor...' : 'Ücretsiz Danışmanlık Talep Et'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
